// @generated-imports
import { findGameById, playGame, showToast, truncate } from './app.js';
import { CONFIG, state } from './core.js';
import { openConfirmLayer } from './layers.js';
import { localRomAsGame } from './library.js';
import { markCoreReady } from './offline.js';
import { getPlayTime, getSetting } from './platform.js';
import { SESSION_STATUS, isCurrentSession } from './session.js';
import { listStateRecords, saveSessionState, stateIdentity } from './states.js';
import { evaluateAchievements, setCounterMax } from './stats.js';
import { startTimeline, stopTimeline, timelineEnabled } from './timeline.js';
// @generated-imports
/* ===========================================================================
   SALVAR AUTOMÁTICO PERIÓDICO e RECUPERAÇÃO APÓS QUEDA (v2.1)
   ---------------------------------------------------------------------------
   • A cada N minutos de `playing` (⚙️, padrão 5), grava o slot automático.
   • Uma marca "sessão aberta" no localStorage (renovada a cada minuto) só é
     apagada quando a sessão termina normalmente. Se a aba travar, a bateria
     acabar ou a TV box reiniciar, a próxima abertura encontra a marca,
     contabiliza o tempo de jogo até a última renovação e oferece retomar do
     último ponto salvo.
   • Também registra "jogado por último em" (estatísticas) e aciona os
     demais serviços da sessão (voltar no tempo, núcleos offline).
   ======================================================================== */

const HEARTBEAT_MS = 60000;

function autosaveIntervalMs() {
  const minutes = Number(getSetting('autosaveInterval')) || 0;
  return minutes > 0 ? minutes * 60000 : 0;
}

/** Idempotente: chamado ao começar a jogar e quando as capacidades chegam. */
function ensureSessionServices(session) {
  if (!isCurrentSession(session) || session.status !== SESSION_STATUS.PLAYING) return;
  if (!session.heartbeatTimer) {
    writeOpenSessionMarker(session);
    session.heartbeatTimer = window.setInterval(() => writeOpenSessionMarker(session), HEARTBEAT_MS);
  }
  if (session.capabilities.state) {
    const ms = autosaveIntervalMs();
    if (ms && !session.autosaveTimer) session.autosaveTimer = window.setInterval(() => periodicAutosave(session), ms);
    if (!session.timelineTimer && timelineEnabled()) startTimeline(session);
  }
}

async function periodicAutosave(session) {
  if (!isCurrentSession(session) || session.status !== SESSION_STATUS.PLAYING || session.menuOpen) return null;
  if (!session.capabilities.state) return null;
  const record = await saveSessionState(session, { kind: 'auto', timeoutMs: 4000 });
  if (record) writeOpenSessionMarker(session, { lastAutoAt: record.savedAt });
  return record;
}

function stopSessionServices(session) {
  if (!session) return;
  ['autosaveTimer', 'heartbeatTimer'].forEach((key) => {
    if (session[key]) { window.clearInterval(session[key]); session[key] = 0; }
  });
  stopTimeline(session);
}

/* --- Marca de sessão aberta --------------------------------------------- */

function readOpenSessionMarker() {
  try {
    const marker = JSON.parse(localStorage.getItem(CONFIG.localStorageOpenSessionKey) || 'null');
    return marker && marker.gameId ? marker : null;
  } catch (_) { return null; }
}

function writeOpenSessionMarker(session, extra = {}) {
  const previous = readOpenSessionMarker();
  const marker = {
    gameId: session.gameId,
    core: session.core,
    title: session.title,
    isLocal: session.kind === 'local',
    startedAt: session.startedPlayingAt || Date.now(),
    heartbeatAt: Date.now(),
    lastAutoAt: extra.lastAutoAt || (previous && previous.gameId === session.gameId ? previous.lastAutoAt : 0) || 0,
  };
  try { localStorage.setItem(CONFIG.localStorageOpenSessionKey, JSON.stringify(marker)); } catch (_) {}
  return marker;
}

function clearOpenSessionMarker() {
  try { localStorage.removeItem(CONFIG.localStorageOpenSessionKey); } catch (_) {}
}

/** Boot: a última sessão não terminou? Contabiliza o tempo e oferece retomar. */
async function checkCrashRecovery() {
  const marker = readOpenSessionMarker();
  if (!marker) return false;
  clearOpenSessionMarker();
  const seconds = Math.max(0, Math.round(((marker.heartbeatAt || marker.startedAt) - marker.startedAt) / 1000));
  if (seconds >= 5) {
    state.playTime[marker.gameId] = getPlayTime(marker.gameId) + seconds;
    try { localStorage.setItem(CONFIG.localStoragePlayTimeKey, JSON.stringify(state.playTime)); } catch (_) {}
  }
  const game = marker.isLocal
    ? (state.localRoms.has(marker.gameId) ? localRomAsGame(state.localRoms.get(marker.gameId)) : null)
    : findGameById(marker.gameId);
  if (!game) return false;
  let records = [];
  try { records = (await listStateRecords(stateIdentity(game.id, game.core))).filter((r) => r.slot !== 'previous'); } catch (_) { records = []; }
  const best = records[0];
  if (!best) {
    showToast(`A última partida de "${truncate(game.title, 30)}" não foi encerrada normalmente e não havia ponto salvo.`);
    return false;
  }
  openConfirmLayer({
    title: 'Retomar de onde parou?',
    message: `A partida de ${game.title} não foi encerrada normalmente (a aba fechou, travou ou o aparelho reiniciou). O último ponto salvo é de ${new Date(best.savedAt).toLocaleString('pt-BR')}.`,
    actions: [{ label: 'Retomar', handler: () => playGame(game, { resumeRecord: best }) }],
    cancelLabel: 'Agora não',
  });
  return true;
}

/* --- "Jogado por último" ------------------------------------------------ */

function loadLastPlayed() {
  try { return JSON.parse(localStorage.getItem(CONFIG.localStorageLastPlayedKey) || '{}') || {}; } catch (_) { return {}; }
}

function recordLastPlayed(gameId) {
  if (!gameId) return;
  const map = loadLastPlayed();
  map[gameId] = Date.now();
  try { localStorage.setItem(CONFIG.localStorageLastPlayedKey, JSON.stringify(map)); } catch (_) {}
}

/* --- Ganchos chamados pelo controlador de sessão ------------------------ */

function onSessionPlayingExtras(session) {
  recordLastPlayed(session.gameId);
  markCoreReady(session);
  ensureSessionServices(session);
}

function onSessionEndExtras(session) {
  clearOpenSessionMarker();
  stopSessionServices(session);
  if (session.wasPlaying && session.startedPlayingAt) {
    const minutes = Math.round((Date.now() - session.startedPlayingAt) / 60000);
    setCounterMax('longestSessionMin', minutes);
  }
  evaluateAchievements();
}

// @generated-exports
export { HEARTBEAT_MS, autosaveIntervalMs, checkCrashRecovery, clearOpenSessionMarker, ensureSessionServices, loadLastPlayed, onSessionEndExtras, onSessionPlayingExtras, periodicAutosave, readOpenSessionMarker, recordLastPlayed, stopSessionServices, writeOpenSessionMarker };
// @generated-exports
